/**
 * usePostAdEntry — Shared hook for unified post-ad entry logic
 *
 * Usage:
 *   const { triggerPostAd, DraftResumeModal } = usePostAdEntry();
 *   triggerPostAd(targetPath, targetState); // checks for drafts first
 *
 * If user has eligible drafts, shows a continue/new/cancel modal.
 * If no drafts, navigates directly to the target path.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileEdit, Plus, X, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export function usePostAdEntry() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith('ar');

  const [draftModalOpen, setDraftModalOpen] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [pendingTarget, setPendingTarget] = useState({ path: '/add-ad', state: {} });
  const [checking, setChecking] = useState(false);

  const triggerPostAd = useCallback(async (path = '/add-ad', state = {}) => {
    setPendingTarget({ path, state });

    // Only check drafts for /add-ad paths (not books/internships)
    if (!path.startsWith('/add-ad')) {
      navigate(path, { state });
      return;
    }

    setChecking(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate(path, { state });
        return;
      }

      const res = await fetch(`${API}/api/ads/drafts`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        const eligible = (data.drafts || []).filter(
          (d) => d.status === 'draft' && !d.published_at
        );

        if (eligible.length > 0) {
          setDrafts(eligible);
          setDraftModalOpen(true);
          setChecking(false);
          return;
        }
      }
    } catch (e) {
      console.error('Draft check failed:', e);
    }

    setChecking(false);
    navigate(path, { state });
  }, [navigate]);

  const handleContinueDraft = useCallback(() => {
    setDraftModalOpen(false);
    const draft = drafts[0]; // most recent
    navigate('/add-ad', { state: { resumeDraftId: draft.ad_id || draft.id } });
  }, [drafts, navigate]);

  const handleStartNew = useCallback(() => {
    setDraftModalOpen(false);
    navigate(pendingTarget.path, { state: { ...pendingTarget.state, skipDraftCheck: true } });
  }, [pendingTarget, navigate]);

  const handleCancel = useCallback(() => {
    setDraftModalOpen(false);
    setDrafts([]);
  }, []);

  // Non-blocking: auto-dismiss the draft prompt after 8s so it never
  // freezes the user at the "+" entry point. Continues to be
  // dismissable manually via the close/cancel buttons.
  useEffect(() => {
    if (!draftModalOpen) return undefined;
    const timer = setTimeout(handleCancel, 8000);
    return () => clearTimeout(timer);
  }, [draftModalOpen, handleCancel]);

  // Inline modal component
  const DraftResumeModal = draftModalOpen ? (
    <div
      className="fixed inset-x-0 bottom-0 z-[100] flex items-end sm:items-end justify-center p-0 sm:p-4 pointer-events-none"
      data-testid="draft-resume-modal"
    >
      <div
        className="pointer-events-auto bg-white w-full sm:w-auto sm:min-w-[360px] sm:max-w-[420px] sm:rounded-2xl rounded-t-2xl overflow-hidden shadow-2xl ring-1 ring-black/5"
        onClick={(e) => e.stopPropagation()}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {/* Header */}
        <div className="px-5 pt-5 pb-3 flex items-start justify-between">
          <div>
            <h3 className="text-base font-bold text-slate-900">
              {isRTL ? 'لديك مسودة محفوظة' : 'You have a saved draft'}
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              {isRTL
                ? `${drafts.length > 1 ? `${drafts.length} مسودات` : 'مسودة واحدة'} — هل تريد المتابعة؟`
                : `${drafts.length > 1 ? `${drafts.length} drafts` : '1 draft'} — would you like to continue?`}
            </p>
          </div>
          <button
            onClick={handleCancel}
            className="h-8 w-8 rounded-full bg-slate-100 grid place-items-center flex-shrink-0"
            data-testid="draft-modal-close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Draft preview */}
        {drafts[0] && (
          <div className="mx-5 mb-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
            <p className="text-sm font-medium text-slate-800 truncate">
              {drafts[0].title || (isRTL ? 'مسودة بدون عنوان' : 'Untitled draft')}
            </p>
            {drafts[0].draft_data?.category && (
              <p className="text-[11px] text-slate-400 mt-0.5">{drafts[0].draft_data.category}</p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="px-5 pb-5 space-y-2">
          <button
            className="w-full h-12 rounded-xl bg-gradient-to-r from-green-600 to-green-700 text-white font-semibold text-sm flex items-center justify-center gap-2 shadow-md"
            onClick={handleContinueDraft}
            data-testid="draft-continue-btn"
          >
            <FileEdit size={16} />
            {isRTL ? 'متابعة المسودة' : 'Continue draft'}
          </button>
          <button
            className="w-full h-12 rounded-xl border-2 border-slate-200 text-slate-700 font-semibold text-sm flex items-center justify-center gap-2 hover:bg-slate-50 transition-colors"
            onClick={handleStartNew}
            data-testid="draft-new-btn"
          >
            <Plus size={16} />
            {isRTL ? 'إعلان جديد' : 'Start new ad'}
          </button>
          <button
            className="w-full h-9 text-xs text-slate-400 hover:text-slate-600 transition-colors"
            onClick={handleCancel}
            data-testid="draft-cancel-btn"
          >
            {isRTL ? 'إلغاء' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return {
    triggerPostAd,
    DraftResumeModal,
    checking,
  };
}
